/**
 * AvatarBubble — Draggable circular webcam bubble overlay.
 *
 * Shows the user's camera feed in a round, draggable bubble during
 * frameSelect and recording phases.  The bubble is rendered at a fixed
 * position on screen and captured as part of the screen recording.
 *
 * Constraints:
 *   • The bubble MUST always stay inside the recording frame.
 *   • When the frame moves, the bubble moves with it.
 *   • When the user drags the bubble, it is clamped to the frame bounds.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";

/** Resolution multiplier for the offscreen video (higher = sharper on Retina). */
const CAM_RES = 640;

interface AvatarBubbleProps {
  visible: boolean;
  size: number;
  frameRect?: { x: number; y: number; width: number; height: number };
}

const PADDING = 12; // min gap between bubble edge and frame edge

/** Clamp bubble position so it stays fully inside the frame. */
function clampToFrame(
  bx: number,
  by: number,
  bubbleSize: number,
  frame: { x: number; y: number; width: number; height: number },
): { x: number; y: number } {
  const minX = frame.x + PADDING;
  const minY = frame.y + PADDING;
  const maxX = frame.x + frame.width - bubbleSize - PADDING;
  const maxY = frame.y + frame.height - bubbleSize - PADDING;
  return {
    x: Math.max(minX, Math.min(maxX, bx)),
    y: Math.max(minY, Math.min(maxY, by)),
  };
}

export const AvatarBubble: React.FC<AvatarBubbleProps> = ({
  visible,
  size,
  frameRect,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number>(0);

  // Position state (absolute screen px)
  const [pos, setPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [initialized, setInitialized] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const dragStart = useRef({ mx: 0, my: 0, px: 0, py: 0 });

  // Keep track of previous frame to detect frame movement
  const prevFrameRef = useRef(frameRect);

  // ── Initial position: bottom-right of frame ─────────────────────────────
  useEffect(() => {
    if (visible && frameRect && !initialized) {
      const margin = 20;
      setPos(
        clampToFrame(
          frameRect.x + frameRect.width - size - margin,
          frameRect.y + frameRect.height - size - margin,
          size,
          frameRect,
        ),
      );
      setInitialized(true);
      prevFrameRef.current = frameRect;
    }
  }, [visible, frameRect, size, initialized]);

  // ── When frame moves/resizes, push the bubble to stay inside ────────────
  useEffect(() => {
    if (!visible || !frameRect || !initialized || isDragging) {
      prevFrameRef.current = frameRect;
      return;
    }

    const prev = prevFrameRef.current;
    prevFrameRef.current = frameRect;

    if (!prev) {
      return;
    }

    // If frame position changed, shift bubble by the same delta
    const dx = frameRect.x - prev.x;
    const dy = frameRect.y - prev.y;

    setPos((p) => {
      const shifted = { x: p.x + dx, y: p.y + dy };
      return clampToFrame(shifted.x, shifted.y, size, frameRect);
    });
  }, [visible, frameRect, size, initialized, isDragging]);

  // ── Reset when hidden ───────────────────────────────────────────────────
  useEffect(() => {
    if (!visible) {
      setInitialized(false);
    }
  }, [visible]);

  // ── Camera stream + canvas render loop ─────────────────────────────────
  useEffect(() => {
    if (!visible) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      setCameraReady(false);
      return;
    }

    let cancelled = false;

    const startCamera = async () => {
      try {
        // Brief pause so the browser can fully release camera hardware
        // from any previous session.  Safari/Edge on macOS hold the
        // resource longer than Chrome after track.stop(); without this,
        // React StrictMode's unmount+remount cycle causes the second
        // getUserMedia call to receive a black stream.
        await new Promise((r) => setTimeout(r, 300));
        if (cancelled) {
          return;
        }

        // Acquire camera — try ideal constraints first, fall back to
        // bare `video: true` if the browser rejects them (some Mac
        // browsers choke on facingMode when only one camera exists).
        let stream: MediaStream;
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: {
              width: { ideal: CAM_RES },
              height: { ideal: CAM_RES },
              facingMode: "user",
            },
            audio: false,
          });
        } catch {
          stream = await navigator.mediaDevices.getUserMedia({
            video: true,
            audio: false,
          });
        }

        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }

        const videoTrack = stream.getVideoTracks()[0];
        if (!videoTrack || videoTrack.readyState !== "live") {
          console.warn(
            "[AvatarBubble] Video track not live:",
            videoTrack?.readyState,
          );
          stream.getTracks().forEach((t) => t.stop());
          setCameraReady(false);
          return;
        }

        streamRef.current = stream;

        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (!video || !canvas) {
          return;
        }

        // Ensure playback attributes are present for Safari / older WebKit
        video.setAttribute("playsinline", "");
        video.setAttribute("webkit-playsinline", "");
        video.muted = true;

        video.srcObject = stream;

        // Wait until the video is actually producing visible frames.
        // Safari doesn't reliably fire `loadedmetadata` for MediaStream
        // sources, so we combine multiple signals with a polling fallback.
        await new Promise<void>((resolve) => {
          if (cancelled) {
            resolve();
            return;
          }

          let resolved = false;
          const done = () => {
            if (resolved) {
              return;
            }
            resolved = true;
            clearInterval(pollId);
            resolve();
          };

          if (
            video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA &&
            !video.paused
          ) {
            done();
            return;
          }

          video.addEventListener("playing", done, { once: true });

          const tryPlay = () => {
            video.play().catch((err: Error) => {
              console.warn("[AvatarBubble] play() rejected:", err.message);
              setTimeout(() => {
                if (!cancelled && !resolved) {
                  video.play().catch(() => {});
                }
              }, 500);
            });
          };

          if (video.readyState >= HTMLMediaElement.HAVE_METADATA) {
            tryPlay();
          } else {
            video.addEventListener("loadedmetadata", tryPlay, { once: true });
            tryPlay();
          }

          const pollId = setInterval(() => {
            if (cancelled || resolved) {
              clearInterval(pollId);
              return;
            }
            if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
              if (video.paused) {
                video.play().catch(() => {});
              }
              done();
            }
          }, 200);

          setTimeout(done, 5000);
        });

        if (cancelled) {
          return;
        }

        setCameraReady(true);

        // Draw mirrored video frames onto the canvas.
        // This bypasses Edge's <video> CSS compositing bug where
        // transform + border-radius + overflow:hidden = black frame.
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          return;
        }
        const draw = () => {
          if (cancelled) {
            return;
          }
          rafRef.current = requestAnimationFrame(draw);
          if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
            const w = canvas.width;
            const h = canvas.height;
            ctx.save();
            ctx.translate(w, 0);
            ctx.scale(-1, 1);
            ctx.drawImage(video, 0, 0, w, h);
            ctx.restore();
          }
        };
        rafRef.current = requestAnimationFrame(draw);
      } catch (err) {
        console.warn("[AvatarBubble] Camera access failed:", err);
        setCameraReady(false);
      }
    };

    startCamera();

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      setCameraReady(false);
    };
  }, [visible]);

  // ── Drag: start ─────────────────────────────────────────────────────────
  const handleDragStart = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
      dragStart.current = {
        mx: e.clientX,
        my: e.clientY,
        px: pos.x,
        py: pos.y,
      };
    },
    [pos],
  );

  // ── Drag: move + release (clamped to frame) ────────────────────────────
  useEffect(() => {
    if (!isDragging) {
      return;
    }

    const handleMouseMove = (e: MouseEvent) => {
      const dx = e.clientX - dragStart.current.mx;
      const dy = e.clientY - dragStart.current.my;
      let nx = dragStart.current.px + dx;
      let ny = dragStart.current.py + dy;

      if (frameRect) {
        const clamped = clampToFrame(nx, ny, size, frameRect);
        nx = clamped.x;
        ny = clamped.y;
      }

      setPos({ x: nx, y: ny });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, frameRect, size]);

  if (!visible) {
    return null;
  }

  // Canvas resolution — use device pixel ratio for Retina sharpness
  const canvasRes = Math.round(size * (window.devicePixelRatio || 1));

  return (
    <div
      className={`avatar-bubble ${cameraReady ? "avatar-bubble--ready" : ""} ${
        isDragging ? "avatar-bubble--dragging" : ""
      }`}
      style={{
        left: pos.x,
        top: pos.y,
        width: size,
        height: size,
        cursor: isDragging ? "grabbing" : "grab",
      }}
      onMouseDown={handleDragStart}
    >
      {/* Hidden video — purely a frame source for the canvas draw loop
          and recordingService compositing.  Must NOT be displayed directly
          inside the bubble: Edge's compositor blacks out <video> inside
          overflow:hidden + border-radius containers. */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        style={{
          position: "absolute",
          left: -9999,
          top: -9999,
          width: size,
          height: size,
          pointerEvents: "none",
        }}
      />
      {/* Visible canvas — draws mirrored camera frames identically
          across Chrome, Edge, Safari, Firefox (no CSS transform needed) */}
      <canvas
        ref={canvasRef}
        className="avatar-bubble__canvas"
        width={canvasRes}
        height={canvasRes}
        style={{ width: size, height: size }}
      />
      {!cameraReady && (
        <div className="avatar-bubble__placeholder">
          <span className="material-symbols-outlined">videocam</span>
        </div>
      )}
    </div>
  );
};
